import type { Application, Graphics, Sprite, Texture } from 'pixi.js'
import { memo, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import {
  areAmbientParticleConfigsEqual,
  BOGGLE_AMBIENT_DEFAULT_TRANSITION_MS,
  lerpResolvedAmbientParticlesConfig,
  resolveAmbientParticlesConfig,
  type AmbientParticlesConfig,
  type AmbientPresetId,
} from './AmbientParticlesConfig'
import {
  AMBIENT_BEAM_MOTE_DRAW_ALPHA,
  getAmbientBeamMoteLifecycleAlpha,
  getAmbientBeamMoteWorldPosition,
  rebuildAmbientBeamMotes,
  stepAmbientBeamMotes,
  type AmbientBeamMote,
} from './ambientBeamMotes'
import { drawAmbientShellBackground } from './ambientShellBackground'
import {
  getBoggleBoardSurfaceBridgeState,
  resetBoggleBoardSurfaceBridge,
  setBoggleAmbientBoardEffectsListener,
} from './boardSurfaceBridge'
import {
  boggleAmbientBoardEffectsNeedTicker,
  drawBoggleAmbientBoardEffects,
  pruneExpiredBoggleAmbientBoardEffects,
} from './drawBoardEffects'
import classes from './AmbientParticlesLayer.module.css'

type AmbientParticlesLayerProps = {
  className?: string
  /** Merged onto the host node (e.g. opacity transition for deferred hub load). */
  hostStyle?: CSSProperties
  /** Called once after the Pixi runtime is initialized and sized (first frame ready). */
  onAmbientReady?: () => void
  config?: Partial<AmbientParticlesConfig>
  preset?: AmbientPresetId
  /**
   * When true, the particle sim and redraw loop stop (last frame stays visible).
   * Used e.g. when the in-run pause overlay is open.
   */
  animationPaused?: boolean
  /**
   * Blend duration when the resolved config changes. `0` = instant (full respawn path).
   * Default {@link BOGGLE_AMBIENT_DEFAULT_TRANSITION_MS} is applied by RouteShell when unset.
   */
  transitionMs?: number
}

type AmbientParticle = {
  ageMs: number
  alpha: number
  fadeDurationMs: number
  fillColor: number
  fullAlphaDurationMs: number
  size: number
  toneIndex: number
  vx: number
  vy: number
  x: number
  y: number
}

type AmbientRuntime = {
  destroy: () => void
  resize: () => void
  scheduleResize: () => void
  setAnimationPaused: (paused: boolean) => void
  setConfig: (
    config: AmbientParticlesConfig,
    options?: { transitionMs?: number },
  ) => void
  setReducedMotion: (reducedMotion: boolean) => void
}

type AmbientTransition = {
  durationMs: number
  from: AmbientParticlesConfig
  startMs: number
  to: AmbientParticlesConfig
}

const AMBIENT_MOBILE_RESOLUTION_CAP = 1.15
const AMBIENT_DESKTOP_RESOLUTION_CAP = 1.5
const AMBIENT_TONE_BUCKET_COUNT = 3
/** RNG seed for shaft dust motes; intentionally not tied to ambient preset `seed`. */
const AMBIENT_BEAM_MOTE_RNG_SEED = 'caver-ambient-beam-motes'
/** Shared fill: white core → transparent edge; tint via `fill({ color })`. */
const AMBIENT_PARTICLE_SOFT_TEX_SIZE = 64
/** Innermost color stops stay solid white to this fraction of the radius (rest is feathered). */
const AMBIENT_PARTICLE_SOFT_CORE_END = 0.82

function createAmbientParticleSoftFillTexture(TextureCtor: typeof import('pixi.js').Texture): Texture {
  const size = AMBIENT_PARTICLE_SOFT_TEX_SIZE
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('AmbientParticlesLayer: cannot create 2D canvas context for soft particle texture')
  }
  const c = size / 2
  const grd = ctx.createRadialGradient(c, c, 0, c, c, c)
  grd.addColorStop(0, 'rgba(255,255,255,1)')
  grd.addColorStop(AMBIENT_PARTICLE_SOFT_CORE_END, 'rgba(255,255,255,1)')
  grd.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = grd
  ctx.fillRect(0, 0, size, size)
  return TextureCtor.from(canvas)
}

export const AmbientParticlesLayer = memo(function AmbientParticlesLayer({
  className,
  hostStyle,
  onAmbientReady,
  config,
  preset = 'home',
  animationPaused = false,
  transitionMs = BOGGLE_AMBIENT_DEFAULT_TRANSITION_MS,
}: AmbientParticlesLayerProps) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const runtimeRef = useRef<AmbientRuntime | null>(null)
  const onReadyRef = useRef(onAmbientReady)
  onReadyRef.current = onAmbientReady
  const lastPushedResolvedRef = useRef<AmbientParticlesConfig | null>(null)
  const prefersReducedMotion = usePrefersReducedMotion()
  const resolvedConfig = useMemo(
    () => resolveAmbientParticlesConfig(preset, config),
    [config, preset],
  )
  const hostClassName = [classes.host, className ?? ''].filter(Boolean).join(' ')

  useEffect(() => {
    const prev = lastPushedResolvedRef.current
    if (prev !== null && areAmbientParticleConfigsEqual(prev, resolvedConfig)) {
      return
    }

    lastPushedResolvedRef.current = resolvedConfig

    const ms =
      transitionMs === 0 ? 0 : transitionMs > 0 ? transitionMs : BOGGLE_AMBIENT_DEFAULT_TRANSITION_MS

    runtimeRef.current?.setConfig(resolvedConfig, { transitionMs: ms })
  }, [resolvedConfig, transitionMs])

  useEffect(() => {
    runtimeRef.current?.setReducedMotion(prefersReducedMotion)
  }, [prefersReducedMotion])

  useEffect(() => {
    runtimeRef.current?.setAnimationPaused(animationPaused)
  }, [animationPaused])

  useEffect(() => {
    let mounted = true
    let cleanup: (() => void) | undefined

    async function setup() {
      const host = hostRef.current
      if (!host) {
        return
      }

      const { Application, Graphics, Sprite: PixiSprite, Texture: PixiTexture } =
        await import('pixi.js')
      const app = new Application()
      await app.init({
        antialias: true,
        autoDensity: true,
        autoStart: false,
        backgroundAlpha: 0,
        height: Math.max(1, host.clientHeight),
        resolution: getAmbientRenderResolution(),
        width: Math.max(1, host.clientWidth),
      })

      if (!mounted || !hostRef.current) {
        app.destroy({ releaseGlobalResources: false }, { children: true })
        return
      }

      const bgCanvas = document.createElement('canvas')
      const bgTexture = PixiTexture.from(bgCanvas, true)
      bgTexture.dynamic = true
      const bgSprite = new PixiSprite(bgTexture)
      bgSprite.eventMode = 'none'
      app.stage.addChildAt(bgSprite, 0)

      const graphicsLayers = Array.from(
        { length: AMBIENT_TONE_BUCKET_COUNT },
        () => new Graphics(),
      )
      for (const graphics of graphicsLayers) {
        app.stage.addChild(graphics)
      }
      const beamMotesGraphics = new Graphics()
      app.stage.addChild(beamMotesGraphics)
      const gameEffectsGraphics = new Graphics()
      gameEffectsGraphics.eventMode = 'none'
      app.stage.addChild(gameEffectsGraphics)
      app.canvas.className = classes.canvas
      app.canvas.setAttribute('aria-hidden', 'true')
      host.appendChild(app.canvas)

      const particleSoftFillTexture = createAmbientParticleSoftFillTexture(PixiTexture)

      const runtime = createAmbientRuntime({
        app,
        beamMotesGraphics,
        bgCanvas,
        bgSprite,
        bgTexture,
        config: resolvedConfig,
        gameEffectsGraphics,
        getHost: () => hostRef.current,
        graphicsLayers,
        particleSoftFillTexture,
        reducedMotion: prefersReducedMotion,
        shellAnimationPaused: animationPaused,
      })
      runtimeRef.current = runtime

      const observer = new ResizeObserver(() => {
        runtime.scheduleResize()
      })
      observer.observe(hostRef.current)
      runtime.resize()
      onReadyRef.current?.()

      cleanup = () => {
        observer.disconnect()
        runtime.destroy()
        if (runtimeRef.current === runtime) {
          runtimeRef.current = null
        }
      }
    }

    void setup()

    return () => {
      mounted = false
      cleanup?.()
    }
  }, [])

  return (
    <div
      aria-hidden="true"
      className={hostClassName}
      ref={hostRef}
      style={hostStyle}
    />
  )
})

function createAmbientRuntime({
  app,
  beamMotesGraphics,
  bgCanvas,
  bgSprite,
  bgTexture,
  config,
  gameEffectsGraphics,
  getHost,
  graphicsLayers,
  particleSoftFillTexture,
  reducedMotion,
  shellAnimationPaused: initialShellAnimationPaused = false,
}: {
  app: Application
  beamMotesGraphics: Graphics
  bgCanvas: HTMLCanvasElement
  bgSprite: Sprite
  bgTexture: Texture
  config: AmbientParticlesConfig
  gameEffectsGraphics: Graphics
  getHost: () => HTMLDivElement | null
  graphicsLayers: Graphics[]
  particleSoftFillTexture: Texture
  reducedMotion: boolean
  shellAnimationPaused?: boolean
}): AmbientRuntime {
  let currentConfig = config
  let currentReducedMotion = reducedMotion
  let shellAnimationPaused = initialShellAnimationPaused
  let disposed = false
  let height = Math.max(1, app.renderer.height)
  let lastFrameMs: number | null = null
  let particles: AmbientParticle[] = []
  let beamMotes: AmbientBeamMote[] = []
  let random = createRandom(currentConfig.seed)
  let beamMoteRandom = createRandom(AMBIENT_BEAM_MOTE_RNG_SEED)
  let resizeFrameId = 0
  let tickerActive = false
  let transition: AmbientTransition | null = null
  let width = Math.max(1, app.renderer.width)

  const transitionBlendNow = (): AmbientParticlesConfig => {
    if (!transition) {
      return currentConfig
    }
    const now = performance.now()
    const rawT = Math.min(1, (now - transition.startMs) / transition.durationMs)
    if (rawT >= 1) {
      return transition.to
    }
    return lerpResolvedAmbientParticlesConfig(transition.from, transition.to, rawT)
  }

  const finishTransitionIfNeeded = (now: number) => {
    if (!transition) {
      return
    }
    const rawT = (now - transition.startMs) / transition.durationMs
    if (rawT < 1) {
      return
    }
    currentConfig = transition.to
    transition = null
    settleParticlesAfterTransition(currentConfig)
    beamMotes = rebuildAmbientBeamMotes(
      currentConfig.shellBackground.lightBeams,
      width,
      height,
      beamMoteRandom,
      now,
      currentReducedMotion,
    )
    syncTicker(transitionBlendNow())
  }

  const rebuildParticles = (snapshotConfig?: AmbientParticlesConfig) => {
    const cfg = snapshotConfig ?? currentConfig
    random = createRandom(cfg.seed)
    beamMoteRandom = createRandom(AMBIENT_BEAM_MOTE_RNG_SEED)
    particles = Array.from({ length: cfg.particleCount }, () =>
      createParticle(cfg, width, height, random, true),
    )
    beamMotes = rebuildAmbientBeamMotes(
      cfg.shellBackground.lightBeams,
      width,
      height,
      beamMoteRandom,
      performance.now(),
      currentReducedMotion,
    )
  }

  /** After a lerped transition: reseed RNG and add motes if under target; extras over target expire naturally in `step`. */
  const settleParticlesAfterTransition = (cfg: AmbientParticlesConfig) => {
    random = createRandom(cfg.seed)
    const target = cfg.particleCount
    while (particles.length < target) {
      particles.push(createParticle(cfg, width, height, random, false))
    }
  }

  const safeRender = () => {
    if (disposed || app.renderer === null || app.stage === null) {
      return
    }
    try {
      app.render()
    } catch {
      /* SPA remount / Fast Refresh can race Pixi's shared batch pool; skip torn-down frames */
    }
  }

  const draw = (simConfig: AmbientParticlesConfig) => {
    if (disposed || app.renderer === null) {
      return
    }

    const nowMs = performance.now()

    const bgCtx = bgCanvas.getContext('2d')
    if (bgCtx) {
      const rw = Math.max(1, Math.round(width))
      const rh = Math.max(1, Math.round(height))
      if (bgCanvas.width !== rw || bgCanvas.height !== rh) {
        bgCanvas.width = rw
        bgCanvas.height = rh
      }

      drawAmbientShellBackground(
        bgCtx,
        rw,
        rh,
        simConfig.shellBackground,
        nowMs,
        currentReducedMotion,
      )
      bgTexture.source.update()
      bgTexture.update()
      bgSprite.width = rw
      bgSprite.height = rh
    }

    for (const graphics of graphicsLayers) {
      graphics.clear()
    }
    beamMotesGraphics.clear()

    const beams = simConfig.shellBackground.lightBeams
    if (beams.length > 0 && beamMotes.length > 0) {
      for (const mote of beamMotes) {
        const position = getAmbientBeamMoteWorldPosition(
          mote,
          beams,
          width,
          height,
          nowMs,
          currentReducedMotion,
        )
        if (position === null) {
          continue
        }

        const lifeAlpha = getAmbientBeamMoteLifecycleAlpha(mote)
        const fillAlpha = lifeAlpha * AMBIENT_BEAM_MOTE_DRAW_ALPHA
        if (fillAlpha <= 0) {
          continue
        }

        beamMotesGraphics.circle(position.x, position.y, mote.size).fill({
          alpha: fillAlpha,
          color: mote.color,
          texture: particleSoftFillTexture,
          textureSpace: 'local',
        })
      }
    }

    if (simConfig.opacity > 0 && particles.length > 0) {
      for (const [toneIndex, graphics] of graphicsLayers.entries()) {
        for (const particle of particles) {
          if (particle.toneIndex !== toneIndex) {
            continue
          }

          const lifecycleAlpha = getParticleLifecycleAlpha(particle)
          if (lifecycleAlpha <= 0) {
            continue
          }

          const fillAlpha = simConfig.opacity * particle.alpha * lifecycleAlpha
          if (fillAlpha <= 0) {
            continue
          }

          graphics.circle(particle.x, particle.y, particle.size).fill({
            alpha: fillAlpha,
            color: particle.fillColor,
            texture: particleSoftFillTexture,
            textureSpace: 'local',
          })
        }
      }
    }

    pruneExpiredBoggleAmbientBoardEffects(nowMs)
    const bridge = getBoggleBoardSurfaceBridgeState()
    const host = getHost()
    if (bridge.snapshot && host) {
      drawBoggleAmbientBoardEffects(
        gameEffectsGraphics,
        bridge.snapshot,
        host.getBoundingClientRect(),
        width,
        height,
        bridge.effects,
        nowMs,
      )
    } else {
      gameEffectsGraphics.clear()
    }

    safeRender()
  }

  const step = (deltaSeconds: number, simConfig: AmbientParticlesConfig) => {
    const nowMs = performance.now()

    stepAmbientBeamMotes(
      beamMotes,
      simConfig.shellBackground.lightBeams,
      width,
      height,
      deltaSeconds,
      nowMs,
      currentReducedMotion,
      beamMoteRandom,
    )

    const padX = width * simConfig.spawnPaddingRatio
    const padY = height * simConfig.spawnPaddingRatio
    const minX = -padX
    const maxX = width + padX
    const minY = -padY
    const maxY = height + padY
    const cap = simConfig.particleCount

    for (let index = particles.length - 1; index >= 0; index -= 1) {
      const particle = particles[index]!
      particle.x += particle.vx * deltaSeconds
      particle.y += particle.vy * deltaSeconds
      particle.ageMs += deltaSeconds * 1000

      if (particle.x < minX) {
        particle.x = maxX
      } else if (particle.x > maxX) {
        particle.x = minX
      }

      if (particle.y < minY) {
        particle.y = maxY
      } else if (particle.y > maxY) {
        particle.y = minY
      }

      if (particle.ageMs >= getParticleTotalLifetimeMs(particle)) {
        if (particles.length > cap) {
          particles.splice(index, 1)
        } else {
          particles[index] = createParticle(simConfig, width, height, random, false)
        }
      }
    }

    while (particles.length < cap) {
      particles.push(createParticle(simConfig, width, height, random, false))
    }
  }

  const tick = () => {
    if (disposed) {
      return
    }
    const now = performance.now()
    finishTransitionIfNeeded(now)
    const simConfig = transitionBlendNow()
    const deltaMs =
      lastFrameMs === null ? 0 : Math.min(100, Math.max(0, now - lastFrameMs))
    lastFrameMs = now
    step(deltaMs / 1000, simConfig)
    draw(simConfig)
    syncTicker(simConfig)
  }

  const syncTicker = (simConfig: AmbientParticlesConfig) => {
    if (disposed) {
      return
    }
    app.ticker.maxFPS = simConfig.maxFps === 'uncapped' ? 0 : simConfig.maxFps

    const boardEffectsActive = boggleAmbientBoardEffectsNeedTicker(
      getBoggleBoardSurfaceBridgeState().effects,
      performance.now(),
    )
    const shouldRun =
      !shellAnimationPaused &&
      ((!currentReducedMotion && simConfig.opacity > 0) || boardEffectsActive)

    if (shouldRun && !tickerActive) {
      lastFrameMs = null
      app.ticker.add(tick)
      app.ticker.start()
      tickerActive = true
      return
    }

    if (!shouldRun && tickerActive) {
      app.ticker.remove(tick)
      tickerActive = false
    }
  }

  const applyResize = () => {
    if (disposed || app.renderer === null) {
      return
    }
    const host = getHost()
    if (!host) {
      return
    }

    width = Math.max(1, host.clientWidth)
    height = Math.max(1, host.clientHeight)
    app.renderer.resize(width, height)
    app.canvas.style.width = `${width}px`
    app.canvas.style.height = `${height}px`
    rebuildParticles(transitionBlendNow())
    const afterResize = transitionBlendNow()
    draw(afterResize)
    syncTicker(afterResize)
  }

  const scheduleResize = () => {
    if (disposed || resizeFrameId !== 0) {
      return
    }
    resizeFrameId = requestAnimationFrame(() => {
      resizeFrameId = 0
      if (disposed) {
        return
      }
      applyResize()
    })
  }

  setBoggleAmbientBoardEffectsListener(() => {
    if (disposed) {
      return
    }

    draw(transitionBlendNow())
    syncTicker(transitionBlendNow())
  })

  rebuildParticles()
  draw(currentConfig)
  syncTicker(currentConfig)

  return {
    destroy: () => {
      disposed = true
      setBoggleAmbientBoardEffectsListener(null)
      if (resizeFrameId !== 0) {
        cancelAnimationFrame(resizeFrameId)
        resizeFrameId = 0
      }
      if (tickerActive) {
        app.ticker.remove(tick)
        tickerActive = false
      }
      app.ticker.stop()
      /**
       * `destroy(true)` runs GlobalResourceRegistry.release(), which destroys shared batch-pool
       * Batch instances (textures=null). Remounted apps can then pull those from the pool and
       * crash in Batcher.break → textureBatch.clear (Pixi SPA issue github.com/pixijs/pixijs/issues/11765).
       */
      resetBoggleBoardSurfaceBridge()
      app.destroy({ releaseGlobalResources: false }, { children: true })
      particleSoftFillTexture.destroy(true)
    },
    resize: applyResize,
    scheduleResize,
    setAnimationPaused: nextPaused => {
      if (disposed) {
        return
      }
      shellAnimationPaused = nextPaused
      const simConfig = transitionBlendNow()
      syncTicker(simConfig)
    },
    setConfig: (nextConfig, options) => {
      if (disposed) {
        return
      }
      const duration = options?.transitionMs ?? 0
      if (duration > 0) {
        const from = transitionBlendNow()
        transition = {
          durationMs: duration,
          from,
          startMs: performance.now(),
          to: nextConfig,
        }
        syncTicker(transitionBlendNow())
        return
      }

      transition = null
      const needsParticleRebuild = !areAmbientParticleConfigsEqual(currentConfig, nextConfig)
      currentConfig = nextConfig
      if (needsParticleRebuild) {
        rebuildParticles()
      }
      draw(currentConfig)
      syncTicker(currentConfig)
    },
    setReducedMotion: nextReducedMotion => {
      if (disposed) {
        return
      }
      currentReducedMotion = nextReducedMotion
      const simConfig = transitionBlendNow()
      beamMotes = rebuildAmbientBeamMotes(
        simConfig.shellBackground.lightBeams,
        width,
        height,
        beamMoteRandom,
        performance.now(),
        currentReducedMotion,
      )
      draw(simConfig)
      syncTicker(simConfig)
    },
  }
}

function createParticle(
  config: AmbientParticlesConfig,
  width: number,
  height: number,
  random: () => number,
  staggerAge: boolean,
): AmbientParticle {
  const padX = width * config.spawnPaddingRatio
  const padY = height * config.spawnPaddingRatio
  const angle = random() * Math.PI * 2
  const speed =
    config.speedMin + random() * Math.max(0, config.speedMax - config.speedMin)

  const fadeSpread = Math.max(0, config.fadeDurationMaxMs - config.fadeDurationMinMs)
  const fadeDurationMs = config.fadeDurationMinMs + random() * fadeSpread
  const fullAlphaDurationMs =
    config.fullAlphaDurationMinMs +
    random() * Math.max(0, config.fullAlphaDurationMaxMs - config.fullAlphaDurationMinMs)
  const totalLifetimeMs = fadeDurationMs * 2 + fullAlphaDurationMs
  const sizeRange = Math.max(0, config.sizeMax - config.sizeMin)
  const size = config.sizeMin + random() * sizeRange
  const sizeRatio = sizeRange === 0 ? 0 : (size - config.sizeMin) / sizeRange
  const alphaMaxForSize =
    config.alphaMax - (config.alphaMax - config.alphaMin) * 0.65 * sizeRatio

  const toneIndex = Math.min(
    AMBIENT_TONE_BUCKET_COUNT - 1,
    Math.floor(random() * AMBIENT_TONE_BUCKET_COUNT),
  )
  const toneOffset = (toneIndex - 1) * (config.hueShift / 2)
  const jitterSpread = config.hueJitterMaxDeg - config.hueJitterMinDeg
  const hueJitter = config.hueJitterMinDeg + random() * jitterSpread
  const saturation =
    config.saturationMin + random() * Math.max(0, config.saturationMax - config.saturationMin)
  const lightness =
    config.lightnessMin + random() * Math.max(0, config.lightnessMax - config.lightnessMin)
  const fillColor = hslToPixiColor(
    config.baseHue + toneOffset + hueJitter,
    saturation,
    lightness,
  )

  return {
    ageMs: staggerAge ? random() * totalLifetimeMs : 0,
    alpha: config.alphaMin + random() * Math.max(0, alphaMaxForSize - config.alphaMin),
    fadeDurationMs,
    fillColor,
    fullAlphaDurationMs,
    size,
    toneIndex,
    vx: Math.cos(angle) * speed + config.driftX,
    vy: Math.sin(angle) * speed + config.driftY,
    x: random() * (width + padX * 2) - padX,
    y: random() * (height + padY * 2) - padY,
  }
}

function getParticleLifecycleAlpha(particle: AmbientParticle) {
  if (particle.ageMs < particle.fadeDurationMs) {
    return easeInOutSine(particle.ageMs / particle.fadeDurationMs)
  }

  const fadeOutStartsMs = particle.fadeDurationMs + particle.fullAlphaDurationMs
  if (particle.ageMs < fadeOutStartsMs) {
    return 1
  }

  return easeInOutSine(1 - (particle.ageMs - fadeOutStartsMs) / particle.fadeDurationMs)
}

function getParticleTotalLifetimeMs(particle: AmbientParticle) {
  return particle.fadeDurationMs * 2 + particle.fullAlphaDurationMs
}

function easeInOutSine(value: number) {
  const clamped = Math.min(1, Math.max(0, value))
  return -(Math.cos(Math.PI * clamped) - 1) / 2
}

function getAmbientRenderResolution() {
  if (typeof window === 'undefined') {
    return 1
  }

  const shortestSide = Math.min(window.innerWidth, window.innerHeight)
  const maxResolution =
    shortestSide < 760 ? AMBIENT_MOBILE_RESOLUTION_CAP : AMBIENT_DESKTOP_RESOLUTION_CAP

  return Math.min(maxResolution, Math.max(1, window.devicePixelRatio || 1))
}

function createRandom(seed: AmbientParticlesConfig['seed']) {
  if (seed === undefined) {
    return Math.random
  }

  let value = typeof seed === 'number' ? seed : hashStringSeed(seed)
  return () => {
    value += 0x6d2b79f5
    let mixed = value
    mixed = Math.imul(mixed ^ (mixed >>> 15), mixed | 1)
    mixed ^= mixed + Math.imul(mixed ^ (mixed >>> 7), mixed | 61)
    return ((mixed ^ (mixed >>> 14)) >>> 0) / 4294967296
  }
}

function hashStringSeed(seed: string) {
  let hash = 2166136261
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }

  return hash >>> 0
}

function hslToPixiColor(
  hue: number,
  saturationPercent: number,
  lightnessPercent: number,
) {
  const h = normalizeHue(hue) / 360
  const s = saturationPercent / 100
  const l = lightnessPercent / 100
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const r = hueToRgb(p, q, h + 1 / 3)
  const g = hueToRgb(p, q, h)
  const b = hueToRgb(p, q, h - 1 / 3)

  return (Math.round(r * 255) << 16) + (Math.round(g * 255) << 8) + Math.round(b * 255)
}

function hueToRgb(p: number, q: number, t: number) {
  let value = t
  if (value < 0) {
    value += 1
  }
  if (value > 1) {
    value -= 1
  }
  if (value < 1 / 6) {
    return p + (q - p) * 6 * value
  }
  if (value < 1 / 2) {
    return q
  }
  if (value < 2 / 3) {
    return p + (q - p) * (2 / 3 - value) * 6
  }

  return p
}

function normalizeHue(hue: number) {
  return ((hue % 360) + 360) % 360
}

function usePrefersReducedMotion() {
  /** Dev builds always run ambient motion so tuning matches production without OS prefs. */
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  useEffect(() => {
    if (import.meta.env.DEV) {
      return
    }

    if (typeof window === 'undefined' || !window.matchMedia) {
      return
    }

    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => {
      setPrefersReducedMotion(media.matches)
    }

    update()
    media.addEventListener('change', update)
    return () => {
      media.removeEventListener('change', update)
    }
  }, [])

  return prefersReducedMotion
}
